import { useEffect, useState } from "react";
import { Box, Tooltip } from "@mui/material";
import BasicDataTable from "../Common/BasicDatatable";
import { MRT_ColumnDef } from "material-react-table";
import AnalyticsAPIClient from "../../api/analyticsAPIs.ts";
import { format } from "date-fns";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar.tsx";
import ProgressSpinner from "../Common/ProgressSpinner.tsx";
import CustomButton from "../Common/Button.tsx";

const DownloadReport = ({
  reportData,
  loading,
}: {
  reportData: any;
  loading: boolean;
}) => {
  const fileList = reportData?.data?.data?.file_list || [];
  const { showSnackbar } = useDisplaySnackbar();

  const rows = fileList.map((file: any, index: number) => ({
    id: index + 1,
    title: file.file_name,
    date: file.last_modified,
    s3_path: file.s3_path,
  }));

  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  const {
    isSuccess: isDownloadSuccess,
    isError: isDownloadError,
    isLoading: isDownloadLoading,
    isFetching: isDownloadFetching,
    data: downloadData,
    error: downloadError,
    refetch: refetchDownload,
  } = AnalyticsAPIClient.useDownloadUrl(
    {
      download_url: downloadUrl || "",
    },
    false 
  );

  // TRIGGER API when downloadUrl changes
  useEffect(() => {
    if (downloadUrl) {
      refetchDownload();
    }
  }, [downloadUrl]);

  // Handle download when API returns download_url
  useEffect(() => {
    if (isDownloadSuccess) {
      const fileData = downloadData?.data?.data?.download_url;
  
      if (fileData) {
        showSnackbar("success", "Downloading started");
        window.open(fileData, "_blank", "noreferrer");
      } else {
        showSnackbar("error", "Download failed: No file URL found.");
      }
  
      // Reset state after action
      setDownloadUrl(null);
    }
  
    if (isDownloadError) {
      showSnackbar("error", downloadError || "Download request failed");
      setDownloadUrl(null);
    }
  }, [isDownloadSuccess, isDownloadError, isDownloadLoading, isDownloadFetching]);
  

  const handleDownload = (s3_path: string) => {
    setDownloadUrl(s3_path);
  };  

  const columns: MRT_ColumnDef<any>[] = [
    {
      accessorKey: "title",
      header: "Report Title",
      minSize: 150,
      size: 400,
      Cell: ({ row }) => (
        <Tooltip title={row.original.title}>
          <Box className="truncate-table-cell-ellipsis">
            {row.original.title}
          </Box>
        </Tooltip>
      ),
    },
    {
      accessorKey: "date",
      header: "Date",
      minSize: 100,
      size: 200,
      Cell: ({ row }) => (
        <Box>{format(new Date(row.original.date), "MMM d, yyyy")}</Box>
      ),
    },
    {
      accessorKey: "download",
      header: "Download Report",
      enableSorting: false,
      enableColumnFilter: false,
      minSize: 120,
      size: 200,
      Cell: ({ row }) => (
        <CustomButton
          type="SmallBrandGradient"
          clickHandler={() => handleDownload(row.original.s3_path)}
          parent_page="Analytics"
        />
      ),
    }    
  ];

  return (
    <Box className="w-full">
      <BasicDataTable
        columns={columns}
        rows={rows}
        rowCount={rows.length}
        hidePaginationFooter={true}
      />
      <ProgressSpinner showSpinner={loading} />
    </Box>
  );
};

export default DownloadReport;
